import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"
import { appVersion, compareVersions } from "./nativeApp";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Open directions or a map search.
 *
 * Inside the app these go through the native bridge rather than window.open, for two reasons.
 * The practical one: Android's WebView creates and then silently drops a window.open request,
 * so "Cómo llegar" did nothing there. The better one: the bridge can hand the OS a maps: or geo:
 * URL, which opens Apple Maps or whichever map app the resident actually uses — and means their
 * destination is no longer handed to Google on every single tap. On the web nothing changes.
 */
function openExternal(nativeUrl, webUrl) {
  if (typeof window === 'undefined') return;
  if (window.__isNativeApp && window.ReactNativeWebView) {
    // Which URL the shell will take. OPEN_EXTERNAL accepts geo: and maps: only from 1.9.9 on
    // (older shells take https, mailto, tel and webcal, and silently drop the rest), and the store
    // 1.9.8 may predate even that list: «Cómo llegar» did nothing on 1.9.5–1.9.8. Those shells get
    // the Google Maps web link, which every shell hands to the OS; a shell too old to report its
    // version has no OPEN_EXTERNAL either and keeps the window.open main always used.
    const v = appVersion();
    if (v && compareVersions(v, '1.9.9') >= 0) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'OPEN_EXTERNAL', url: nativeUrl }));
      return;
    }
    if (v && compareVersions(v, '1.9.1') >= 0) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'OPEN_EXTERNAL', url: webUrl }));
      return;
    }
  }
  window.open(webUrl, '_blank', 'noopener,noreferrer');
}

const isIOSApp = () =>
  typeof window !== 'undefined' && window.__appPlatform === 'ios';

export function openDirections(lat, lon, mode = 'walking') {
  const web = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&travelmode=${mode}`;
  // Apple Maps takes a travel-mode flag; geo: has no standard one, so Android falls back to the
  // app's own default rather than silently pretending the mode was honoured.
  const native = isIOSApp()
    ? `maps://?daddr=${lat},${lon}&dirflg=${mode === 'driving' ? 'd' : 'w'}`
    : `geo:${lat},${lon}?q=${lat},${lon}`;
  openExternal(native, web);
}

/**
 * Reroute Google Maps links to the phone's own map app, once, for the whole app.
 *
 * A dozen places render a plain <a href="https://www.google.com/maps/...">, which is correct
 * markup and works — it leaves through the request gate and the OS opens it. But it opens
 * Google's web map, and it hands a resident's destination to Google on every tap.
 *
 * Intercepting at the document level rather than rewriting each link keeps them real links —
 * focusable, middle-clickable, readable by a screen reader as links — and covers the ones someone
 * adds next. Only active inside the app; the web keeps its ordinary behaviour.
 */
export function installMapLinkInterceptor() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (!window.__isNativeApp || !window.ReactNativeWebView) return;
  if (window.__mapLinksIntercepted) return;
  window.__mapLinksIntercepted = true;

  document.addEventListener('click', (e) => {
    const a = e.target?.closest?.('a[href]');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (!/^https?:\/\/(www\.)?google\.[a-z.]+\/maps/i.test(href)) return;
    // A shell too old to say its version has no OPEN_EXTERNAL: the ordinary link, which its
    // request gate hands to the OS, is what works there.
    const v = appVersion();
    if (!v || compareVersions(v, '1.9.1') < 0) return;

    let url;
    try { url = new URL(href); } catch { return; }
    const p = url.searchParams;
    const coords = /^(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/;

    const dest = p.get('destination');
    const destMatch = dest && coords.exec(dest);
    if (destMatch) {
      e.preventDefault();
      openDirections(destMatch[1], destMatch[2], p.get('travelmode') || 'walking');
      return;
    }
    const q = p.get('query') || p.get('q');
    if (q) {
      e.preventDefault();
      const qMatch = coords.exec(q);
      // A bare lat,lon is a place to go to, not a phrase to look up.
      if (qMatch) openDirections(qMatch[1], qMatch[2]);
      else openMapsSearch(q);
      return;
    }
    // An address rather than coordinates — still better handled by the map app than the browser.
    if (dest) { e.preventDefault(); openMapsSearch(dest); }
  }, true);
}

export function openMapsSearch(query) {
  const web = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  const native = isIOSApp()
    ? `maps://?q=${encodeURIComponent(query)}`
    : `geo:0,0?q=${encodeURIComponent(query)}`;
  openExternal(native, web);
}


/**
 * Hand a share to the native app's share sheet if we're running inside it.
 * Returns true when the bridge accepted it (caller should stop), false otherwise.
 * Android WebView has no navigator.share, so without this the app falls back to
 * clipboard/wa.me and the user never sees the system share sheet. See Fix #35.
 */
export function tryNativeShare({ title, text, url }) {
  if (typeof window !== 'undefined' && window.ReactNativeWebView && window.__nativeShareSupported) {
    // Fold the link into the message rather than relying on the `url` field.
    //
    // Android's Share.share() has no `url` — only `message` and `title` — so a link sent
    // separately is dropped, and this bridge is the Android path (iOS has navigator.share and
    // returns above). The shell now folds it in itself, but a shell update takes days of store
    // review while this deploys in minutes, and every copy of the app already installed keeps
    // the old behaviour until its user updates. Doing it here fixes them all today; the native
    // side skips a link the text already contains, so the two cannot double up.
    // ...but only off iOS. There the shell passes `url` to Share.share() as its own field, so
    // folding it in as well would print the link twice. (iOS reaches this branch only when
    // navigator.share is missing, which is rare — but "rare" is not "never".)
    const link = typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null;
    const foldIn = link && window.__appPlatform !== 'ios' && !(text || '').includes(link);
    const body = foldIn ? `${text || ''}\n${link}`.trim() : text;
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'NATIVE_SHARE', title, text: body, url }));
    return true;
  }
  return false;
}

/**
 * Share content using native share sheet (mobile) or Web Share API (browser).
 * Falls back to clipboard copy if neither works.
 * @param {Object} opts - { title, text, url }
 */
export async function shareContent({ title, text, url }) {
  // Web Share API — browsers and iOS WKWebView (WebKit shows the native sheet itself)
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return true;
    }
  } catch (e) {
    if (e.name === 'AbortError') return false;
  }
  // Native bridge (Fix #35) — Android WebView has no navigator.share on any OEM
  if (tryNativeShare({ title, text, url })) return true;
  // Clipboard fallback
  try {
    await navigator.clipboard.writeText(text);
    alert('Texto copiado al portapapeles');
    return true;
  } catch {
    return false;
  }
}

// One Intl.NumberFormat per option pair, built on first use. toLocaleString(locale, options)
// constructs a new formatter on every call (locale negotiation included), and numEs runs from
// ~90 call sites, many inside lists and charts that re-render: measured at ~52x the cost of
// reusing one. The output is identical, because toLocaleString is specified as exactly this.
const _fmt = new Map();
function _esFormat(min, max) {
  const key = `${min}|${max}`;
  let f = _fmt.get(key);
  if (!f) {
    f = new Intl.NumberFormat('es-ES', { minimumFractionDigits: min, maximumFractionDigits: max });
    _fmt.set(key, f);
  }
  return f;
}

/** Spanish number for display: numEs(47.5) -> '47,5'; numEs(2.3, 2, 2) -> '2,30'. Use instead of toFixed() in UI text. */
export function numEs(v, d = 1, min = 0) {
  if (v == null || v === '' || isNaN(v)) return '—';
  return _esFormat(min, d).format(Number(v));
}

/** Spanish percentage: pctEs(58.68, 2) -> '58,68 %'. Keeps up to `d` decimals as published. */
export function pctEs(v, d = 1) {
  if (v == null || v === '' || isNaN(v)) return '—';
  return `${_esFormat(0, d).format(Number(v))} %`;
}

/** GTFS clock strings run past midnight ("24:55:00" = 00:55 next day); show them as wall-clock HH:MM. */
export function gtfsTime(t) {
  if (!t) return '';
  const [h, m = '00'] = String(t).split(':');
  const hh = Number(h);
  if (Number.isNaN(hh)) return String(t).slice(0, 5);
  return `${String(hh % 24).padStart(2, '0')}:${m.slice(0, 2)}`;
}

/**
 * Background for a solid badge with white text (bus/train line numbers…): the brand colour is
 * darkened just enough for white text to reach WCAG AA 4.5:1, so the hue stays recognisable.
 * Non-hex values are returned unchanged.
 */
export function solidBg(color) {
  const m = typeof color === 'string' && color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return color;
  const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  const rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  const lum = (c) => {
    const [r, g, b] = c.map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  for (let f = 1; f > 0.3; f -= 0.02) {
    const c = rgb.map((v) => Math.round(v * f));
    if (1.05 / (lum(c) + 0.05) >= 4.6) return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
  }
  return '#1e293b';
}

/**
 * Theme-aware text colour for inline `style={{ color }}` values (category colours, status colours…).
 * Mid/dark colours are mixed toward black in the light theme and toward white in the dark theme
 * (via --ink-mix / --ink-pct in index.css), so they stay readable on tinted and dark backgrounds.
 * Light colours (e.g. white text on a coloured chip) are returned unchanged.
 */
export function ink(color) {
  if (!color || typeof color !== 'string') return color;
  const m = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (m) {
    const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
    // Linearise before weighting. These coefficients describe relative luminance and are only
    // meaningful on linear-light values; applied to raw sRGB they overstate every mid-tone, so
    // colours that needed darkening were waved through as "already light". #FCD34D scored 0.824
    // that way against a true luminance of 0.678 -- which is how the ERA occupancy badge ended
    // up at 1.38:1 against its own chip.
    const lin = (v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
    if (0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b) > 0.7) return color;
  } else if (!/^(rgb|hsl)/i.test(color.trim())) {
    return color;
  }
  return `color-mix(in srgb, ${color}, var(--ink-mix) var(--ink-pct))`;
}

/** Decode the HTML entities that survive in feed text (WordPress excerpts arrive as HTML, so
 *  they reach us with a literal "(&#8230;)" on the end). Numeric plus the handful of named
 *  entities those feeds actually use — deliberately not innerHTML, which would run markup. */
const NAMED_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', hellip: '…', ndash: '–', mdash: '—', laquo: '«', raquo: '»', rsquo: '’', lsquo: '‘', ldquo: '“', rdquo: '”' };
export function decodeEntities(s) {
  if (!s || typeof s !== 'string' || s.indexOf('&') === -1) return s;
  return s
    .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCodePoint(parseInt(n, 16)))
    .replace(/&([a-z]+);/gi, (m, name) => NAMED_ENTITIES[name.toLowerCase()] ?? m);
}

/** Podium colour class for a 0-based leaderboard rank.
 *  The literal Tailwind ambers read as gold/silver/bronze on white but land at 2.2-3.4:1 on the
 *  dark card, and a gold bright enough for dark is unreadable on white — no single palette
 *  passes AA in both themes, so the colours live in index.css with one set per theme. */
export function medalClass(i) {
  return i < 3 ? `rank-medal rank-${i + 1}` : 'rank-medal';
}

/** Read text aloud.
 *
 *  Inside the app this goes through the native bridge: the Web Speech API is unavailable in
 *  WKWebView and unreliable in Android's WebView, so the shell does the speaking. In a desktop
 *  browser it falls back to speechSynthesis, which works there.
 *
 *  Returns false when neither path is available, so callers can hide the control entirely
 *  rather than offering a button that does nothing.
 */
/**
 * Native speech state, as an event rather than a single global.
 *
 * The native side calls window.__onNativeSpeech, and exactly one consumer can own that. Boletines
 * owned it and deleted it on unmount, so the moment a second reader existed anywhere in the app
 * the two would clobber each other and whichever unmounted last would take the bridge down with
 * it. One owner here, a DOM event for everyone else.
 *
 * Returns an unsubscribe. State is 'start' | 'done' | 'stopped' | 'error' | 'unavailable'.
 */
let speechBridgeInstalled = false;
export function onSpeechState(handler) {
  if (typeof window === 'undefined') return () => {};
  if (!speechBridgeInstalled) {
    speechBridgeInstalled = true;
    window.__onNativeSpeech = (state) => {
      window.dispatchEvent(new CustomEvent('somostorre:speech', { detail: state }));
    };
  }
  const wrapped = (e) => handler(e.detail);
  window.addEventListener('somostorre:speech', wrapped);
  return () => window.removeEventListener('somostorre:speech', wrapped);
}

// The browser utterance currently speaking. Cancelling one to start the next fires the old one's
// end/error later; only the current one may report, or it would reset the new reading's button.
let currentUtterance = null;

export function speak(text, { rate = 1 } = {}) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return false;
  if (typeof window !== 'undefined' && window.__nativeSpeechSupported && window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'SPEAK', text: clean, rate }));
    return true;
  }
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(clean);
    utter.lang = 'es-ES';
    utter.rate = rate;
    // Same 'somostorre:speech' states the native bridge sends (see onSpeechState), so a reading
    // that finishes on its own clears "Detener" on the web too.
    const report = (state) => {
      if (utter !== currentUtterance) return;
      if (state !== 'start') currentUtterance = null;
      window.dispatchEvent(new CustomEvent('somostorre:speech', { detail: state }));
    };
    utter.onstart = () => report('start');
    utter.onend = () => report('done');
    utter.onerror = (e) => report(e?.error === 'interrupted' || e?.error === 'canceled' ? 'stopped' : 'error');
    currentUtterance = utter;
    window.speechSynthesis.speak(utter);
    return true;
  }
  return false;
}

/**
 * Turn an answer written to be read into one worth hearing.
 *
 * Our answers are written for a screen, and read aloud they go wrong in specific ways (see §5 of
 * docs/VOICE_TORRE_IA.md): markdown is spelled out, "C/" is read as a letter and a slash, "€/L"
 * and "hm³" are unpronounceable, and a Spanish decimal comma is read as a list separator.
 *
 * Deliberately narrow. It fixes the handful of forms that actually appear in Torre's answers and
 * leaves everything else alone — a clever general-purpose rewriter would mangle street names,
 * which is the one thing this city's answers cannot afford.
 */
export function toSpeech(text) {
  let t = String(text || '');
  t = t.replace(/```[\s\S]*?```/g, ' ');           // code fences
  t = t.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');    // links keep their label, lose the url
  t = t.replace(/[*_`#>]+/g, ' ');                  // emphasis, headings, quotes
  t = t.replace(/^\s*[-•]\s*/gm, ', ');             // bullets become pauses
  t = t.replace(/\bC\/\s*/g, 'calle ');
  t = t.replace(/\bAvda?\.\s*/gi, 'avenida ');
  t = t.replace(/\bPz?a\.\s*/gi, 'plaza ');
  t = t.replace(/(\d)\s*€\s*\/\s*L\b/gi, '$1 euros por litro');
  t = t.replace(/(\d)\s*€\/kWh/gi, '$1 euros por kilovatio hora');
  t = t.replace(/(\d)\s*€/g, '$1 euros');
  t = t.replace(/\bhm³/g, ' hectómetros cúbicos');
  t = t.replace(/\bkm\/h\b/gi, ' kilómetros por hora');
  t = t.replace(/\bmin\b/g, 'minutos');
  // A Spanish decimal comma read literally becomes a pause: "1,79" -> "1 coma 79".
  t = t.replace(/(\d),(\d)/g, '$1 coma $2');
  t = t.replace(/(\d)\s*%/g, '$1 por ciento');
  t = t.replace(/(\d)\s*\/\s*(\d)/g, '$1 de $2');   // "19 hm³ / 21 hm³" reads as a ratio, not a date
  t = t.replace(/\s{2,}/g, ' ').replace(/\s+([,.])/g, '$1');
  t = t.replace(/^[\s,.;:–—-]+/, '');                 // a leading bullet became a leading comma
  return t.trim();
}

/**
 * The first couple of sentences, for reading aloud. A screen can list nineteen pharmacies; a
 * spoken answer gives the gist and stops, because a listener cannot skim.
 */
export function speechPreview(text, maxSentences = 2) {
  const full = toSpeech(text);
  const parts = full.match(/[^.!?]+[.!?]+/g);
  if (!parts || parts.length <= maxSentences) return { preview: full, truncated: false };
  return { preview: parts.slice(0, maxSentences).join(' ').trim(), truncated: true };
}

/** Stop any reading in progress. Safe to call when nothing is playing. */
export function stopSpeaking() {
  if (typeof window === 'undefined') return;
  if (window.__nativeSpeechSupported && window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'STOP_SPEAKING' }));
    return;
  }
  try { window.speechSynthesis?.cancel(); } catch { /* not supported */ }
}

/** True when a "read aloud" control is worth showing at all. */
export function canSpeak() {
  if (typeof window === 'undefined') return false;
  return Boolean((window.__nativeSpeechSupported && window.ReactNativeWebView) || window.speechSynthesis);
}

/** Dictation, the input half of voice.
 *
 *  Same reason as speak(): the Web Speech API is unavailable in WKWebView and unreliable in
 *  Android's WebView, so inside the app the microphone lives in the native shell and the
 *  transcript comes back over the bridge. A desktop browser falls back to its own recogniser.
 *
 *  onUpdate({ text, isFinal }) fires as the words arrive, so the caller can show them appearing
 *  and let the reader correct a mishearing before it is sent — speech recognition is
 *  confidently wrong often enough that silently querying the result feels broken.
 */
export function startDictation({ onUpdate, onEnd, onError } = {}) {
  if (typeof window === 'undefined') return false;

  if (window.__nativeDictationSupported && window.ReactNativeWebView) {
    window.__onNativeDictation = (payload) => {
      if (payload?.state === 'result') onUpdate?.({ text: payload.text || '', isFinal: payload.isFinal });
      else if (payload?.state === 'end') onEnd?.();
      else if (payload?.state === 'error') onError?.(payload.code);
    };
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'SPEECH_START' }));
    return true;
  }

  const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!Recognition) return false;
  const rec = new Recognition();
  rec.lang = 'es-ES';
  rec.interimResults = true;
  rec.continuous = false;
  rec.onresult = (e) => {
    const r = e.results[e.results.length - 1];
    onUpdate?.({ text: r[0]?.transcript || '', isFinal: r.isFinal });
  };
  rec.onend = () => onEnd?.();
  rec.onerror = (e) => onError?.(e.error);
  rec.start();
  window.__webDictation = rec;
  return true;
}

/** Stop a dictation in progress. Safe to call when nothing is listening. */
export function stopDictation() {
  if (typeof window === 'undefined') return;
  if (window.__nativeDictationSupported && window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'SPEECH_STOP' }));
    return;
  }
  try { window.__webDictation?.stop(); } catch { /* not listening */ }
}

/** True when a microphone button is worth showing at all. */
export function canDictate() {
  if (typeof window === 'undefined') return false;
  if (window.__nativeDictationSupported && window.ReactNativeWebView) return true;
  return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

/**
 * Darken `hex` just enough that white text on it clears a contrast ratio (AA body text by
 * default), and no further.
 *
 * The trámite hero paints itself from each trámite's own brand colour, and six of the thirteen
 * were too light for the white text on top: empadronamiento at 3.74:1, the fiscal calendar at
 * 3.56, and registro de mascotas on amber-500 at 2.15 -- the same amber that failed on the
 * Playas badge and twice in the Gasolineras chart. A single fixed scrim cannot solve that set:
 * enough to rescue the amber turns the blues to mud. So darken each colour by however much it
 * actually needs, which also means a colour added later cannot quietly reintroduce the problem.
 */
export function darkenForWhiteText(hex, target = 5.2) {
  const m = (hex || '').trim().match(/^#?([0-9a-f]{6})$/i);
  if (!m) return hex;
  const h = m[1];
  const parts = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  const lin = (v) => { const x = v / 255; return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4); };
  const ratio = (rgb) => 1.05 / (0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]) + 0.05);
  let scale = 1;
  // 2% steps: fine enough that the colour stays recognisable, bounded so this cannot spin.
  while (scale > 0.15 && ratio(parts.map((v) => v * scale)) < target) scale -= 0.02;
  const out = parts.map((v) => Math.round(v * scale));
  return `#${out.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}
